import {PageSideLayout} from '../../../ui/layouts/page-size';
import {useStoreContext} from '../../../store/store-context';
import {observer} from 'mobx-react';
import {useMemo, useState, useEffect, useCallback} from 'react';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import {AdminAnalyticsHook} from './reporting-pages/admin-analytics/admin-analytics.hooks';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router';
import {useSnapshotWidget} from './reporting-pages/reporting-overview/hooks/snapshot-widgets';
import {useIsRouteActive} from '@utils/route-href';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {cfv} from '../../../utils/framework';
import {AnalyticsStatus} from './reporting-pages/admin-analytics/analytics-pages/analytics-status/analytics-status';
import {AnalyticsQuestions} from './reporting-pages/admin-analytics/analytics-pages/analytics-questions';
import {useTags} from '@hooks/tags';
import {FlexRowSpaceBetween} from '@ui/style/styles';
import {Body1, Headline2} from '@ui/atoms/typography';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {AnalyticsActionItems} from './reporting-pages/admin-analytics/analytics-pages/analytics-action-items';
import {ReportingFilter} from './reporting-filter';
import {capitalize} from '@utils';
import {ReportingFilterToggle} from './reporting-filter-toggle';
import {
  FeaturePageContent,
  FeaturePageHeader,
} from '@ui/molecules/feature-page-header';
import {ManagerDashboard} from './manager-dashboard';

const REPORTING_ROUTES = {
  GOALS: `goals`,
  CHECKINS: 'checkins',
  OBJECTIVES: 'objectives',
  KPIs: 'kpis',
  ACTION_ITEMS: `action-items`,
  QUESTIONS: `questions`,
  OVERVIEW: `overview`,
};

const LegacyCompanyReporting = observer(
  ({
    tab,
  }: {
    showOverview?: boolean;
    tab?: 'overview' | 'status' | 'ownership';
  }) => {
    const {
      storeDashboardRoutes,
      authStore: {auth},
      analyticsStore: {
        goalsData,
        setFilterCompletion,
        setFilterStatus,
        setFilterState,
        goalsDate,
        modifyGoalsDate,
        updateSelectedTags,
        updateSearchValue,
        setGoalBreakdownBy,
      },
    } = useStoreContext();

    dayjs.extend(customParseFormat);

    const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);

    const {isPerformanceActive} = useWorkspaceUseCase();

    const {getOptions: overviewWidgetOptions} = useSnapshotWidget();

    const {isRouteActive} = useIsRouteActive();

    const {
      activateCompanyReportingTab,
      companyReportingTabs,
      activateStatusTab,
    } = storeDashboardRoutes;

    AdminAnalyticsHook();

    const isGoalsActive = useMemo(() => isRouteActive(REPORTING_ROUTES.GOALS), [
      isRouteActive,
    ]);

    const isActionItemsActive = useMemo(
      () => isRouteActive(REPORTING_ROUTES.ACTION_ITEMS),
      [isRouteActive],
    );

    const isObjectivesActive = useMemo(
      () => isRouteActive(REPORTING_ROUTES.OBJECTIVES),
      [isRouteActive],
    );

    const isQuestionsActive = useMemo(
      () => isRouteActive(REPORTING_ROUTES.QUESTIONS),
      [isRouteActive],
    );

    const isKPIsActive = useMemo(() => isRouteActive(REPORTING_ROUTES.KPIs), [
      isRouteActive,
    ]);

    const {
      analyticsStore: {setSearchable, setAnalyticsRange},
      storeDashboardRoutes: {
        activateDashboardTab,
        activateAnalyticsDashboardTab,
        GoalViewTabs,
        statusTabs,
      },
    } = useStoreContext();

    useEffect(() => {
      activateDashboardTab('reporting');
      if (isGoalsActive && !companyReportingTabs.status) {
        activateCompanyReportingTab('status');
      }
    }, [
      activateDashboardTab,
      tab,
      activateCompanyReportingTab,
      isGoalsActive,
      companyReportingTabs.status,
    ]);

    const enableOverview = overviewWidgetOptions.length;

    const navigate = useNavigate();

    useEffect(() => {
      if (
        !enableOverview &&
        window.location.pathname.includes(REPORTING_ROUTES.OVERVIEW)
      ) {
        navigate(REPORTING_ROUTES.OBJECTIVES);
      }
    }, [enableOverview, navigate]);

    const [selectedGroups, setSelectedGroups] = useState<
      {label: string; value: string}[]
    >([]);

    const [selectedTeams, setSelectedTeams] = useState<
      {label: string; value: string}[]
    >([]);

    const [selectedMembers, setSelectedMembers] = useState<
      {label: string; value: string}[]
    >([]);

    const [selectedGoalStatusToggles, setGoalStatusToggles] = useState<
      string[]
    >(['past_due', 'on_track', 'behind', 'at_risk', 'no_status']);

    const [breakdownBy, setBreakdownBy] = useState('');

    const {tags} = useTags(`goal_tags`);

    const resetFilter = useCallback(() => {
      setSelectedGroups([]);
      setSelectedMembers([]);
      updateSearchValue('');
      setSearchable('members', '');
      setSearchable('managers', '');
      setSearchable('department', '');
      setSearchable('goalType', '');
      setSelectedTeams([]);
      setAdditionalFilters([]);

      setBreakdownBy('');
    }, [setSearchable, updateSearchValue]);

    const AnalyticsNavigations = useMemo(() => {
      const nav = [];

      if (isPerformanceActive) {
        nav.push({
          active: isObjectivesActive,
          href: REPORTING_ROUTES.OBJECTIVES,
          id: '1',
          onClick: () => {
            activateAnalyticsDashboardTab('objectives');
            activateStatusTab('goal');

            resetFilter();
          },
          title: capitalize(cfv(undefined, true).g_oals),
        });
        if (auth.user.workspace.config.allowKpi) {
          nav.push({
            active: isKPIsActive,
            href: REPORTING_ROUTES.KPIs,
            id: '2',
            onClick: () => {
              activateStatusTab('Kpi');
              setAnalyticsRange('days');
              resetFilter();
            },
            title: 'KPIs',
          });
        }
      }

      return nav;
    }, [
      activateAnalyticsDashboardTab,
      activateStatusTab,
      auth.user.workspace.config.allowKpi,
      isKPIsActive,
      isObjectivesActive,
      isPerformanceActive,
      resetFilter,
      setAnalyticsRange,
    ]);

    useEffect(() => {
      if (isKPIsActive && !statusTabs.Kpi) {
        activateStatusTab('Kpi');
        setAnalyticsRange('days');
      }
    }, [activateStatusTab, isKPIsActive, setAnalyticsRange, statusTabs.Kpi]);

    const activeFilter = () => {
      if (isActionItemsActive) return 'actionItems';
      if (isObjectivesActive) return 'objectives';
      if (isKPIsActive) return 'kpis';
      if (isQuestionsActive) return 'questions';

      return 'objectives';
    };

    const [showObjectiveStatus, setShowObjectiveStatus] = useState(true);

    const canUpdateGoals = ['objectives', 'kpis'].includes(activeFilter());

    const [showFilter, setShowFilter] = useState(true);

    return (
      <div style={{paddingBottom: 100}}>
        <FeaturePageHeader
          ariaLabel="Reporting views"
          width="wide"
          tabs={AnalyticsNavigations.map((item) => ({
            id: item.id,
            label: String(item.title),
            active: item.active,
            onClick: () => {
              item.onClick();
              navigate(`/reporting/${item.href}`);
            },
          }))}
          actions={
            <ReportingFilterToggle
              isOpen={showFilter}
              onToggle={() => setShowFilter((current) => !current)}
            />
          }
        />

        <FeaturePageContent width="wide">
          <FlexRowSpaceBetween className="mb-6">
            <div className="">
              <Headline2>Reports</Headline2>
              <div className="w-[80%] mt-1">
                <Body1 kind="textBody">
                  View and download insights from team and company-wide
                  activities across your workspace.
                </Body1>
              </div>
            </div>
          </FlexRowSpaceBetween>
          <VerticalSpacer size="24px" />

          <PageSideLayout
            variant={GoalViewTabs.tree ? 'tree' : 'reporting'}
            emptyTreeGoalState={goalsData?.goals.length === 0}
            content={
              <>
                <Routes>
                  <Route
                    path="/action-items"
                    element={
                      <AnalyticsActionItems
                        selectedMembers={selectedMembers}
                        selectedTeams={selectedTeams}
                        selectedGroups={selectedGroups}
                        breakdownBy={breakdownBy}
                      />
                    }
                  />
                  <Route
                    path={'/' + REPORTING_ROUTES.OBJECTIVES}
                    element={
                      <AnalyticsStatus
                        showObjectiveStatus={showObjectiveStatus}
                      />
                    }
                  />
                  <Route
                    path={'/' + REPORTING_ROUTES.KPIs}
                    element={<AnalyticsStatus />}
                  />

                  <Route
                    path={'/' + REPORTING_ROUTES.QUESTIONS}
                    element={
                      <AnalyticsQuestions
                        selectedMembers={selectedMembers}
                        selectedTeams={selectedTeams}
                        selectedGroups={selectedGroups}
                      />
                    }
                  />

                  <Route
                    path={'/' + REPORTING_ROUTES.CHECKINS}
                    element={
                      <Navigate to="/reporting/objectives" replace={true} />
                    }
                  />
                </Routes>
              </>
            }
            navigation={
              showFilter ? (
                <div id="reporting-filter-panel">
                  <ReportingFilter
                    type={activeFilter()}
                    dateRange={goalsDate}
                    additionalFilters={additionalFilters}
                    setAdditionalFilters={setAdditionalFilters}
                    setDateRange={(date: any) =>
                      modifyGoalsDate(date.starts, date.ends, date.period)
                    }
                    selectedGroups={selectedGroups}
                    onChangeTeams={(data) => {
                      setSelectedTeams(data);
                      if (canUpdateGoals) {
                        setSearchable(
                          'managers',
                          data.map((manager) => manager.value).join(','),
                        );
                      }
                    }}
                    selectedTeams={selectedTeams}
                    onChangeMembers={(data) => {
                      setSelectedMembers(data);
                      if (canUpdateGoals) {
                        setSearchable(
                          'members',
                          data.map((member) => member.value).join(','),
                        );
                      }
                    }}
                    selectedMembers={selectedMembers}
                    onChangeGroups={(data) => {
                      setSelectedGroups(data);
                      if (canUpdateGoals) {
                        setSearchable(
                          'department',
                          data.map((department) => department.value).join(','),
                        );
                      }
                    }}
                    breakdownBy={breakdownBy}
                    disableAdditionalFilter={['kpis', 'questions'].includes(
                      activeFilter(),
                    )}
                    onChangeGoalProgress={(data) => {
                      setFilterCompletion('goals', data.join(','));
                    }}
                    onChangeGoalType={(value, groups) => {
                      setSearchable('goalType', value);
                      setSearchable('department', groups?.join(',') || '');
                    }}
                    onChangeGoalState={(value) => {
                      setFilterState('goals', value || 'active,past_due');
                    }}
                    onChangeStatus={(data) => {
                      setGoalStatusToggles(data);
                      setFilterStatus('goals', data.join(','));
                    }}
                    goalStatus={selectedGoalStatusToggles}
                    onChangeGoalTags={(value) => updateSelectedTags(value)}
                    toggleObjectiveStatus={setShowObjectiveStatus}
                    showObjectiveStatus={showObjectiveStatus}
                    kpiTags={tags
                      .filter((tag) => !!tag.tag?.name)
                      .map((tag) => ({
                        value: tag.tag?.name,
                        label: tag.tag?.name,
                      }))}
                    // handleMeetingFilter={setMeetingFilter}
                    setBreakdownBy={(value) => {
                      setBreakdownBy(value);

                      if (activeFilter() === 'objectives') {
                        setGoalBreakdownBy(value);
                      }
                    }}
                  />
                </div>
              ) : null
            }
          />
        </FeaturePageContent>
      </div>
    );
  },
);

export const CompanyReporting = (props: {
  showOverview?: boolean;
  tab?: 'overview' | 'status' | 'ownership';
}) => {
  const location = useLocation();

  if (location.pathname === '/reporting') {
    return <Navigate to="/reporting/overview" replace />;
  }

  if (
    location.pathname === '/reporting/overview/goals' ||
    location.pathname === '/reporting/overview/programs' ||
    location.pathname === '/reporting/overview/feedback' ||
    location.pathname === '/reporting/overview/attention'
  ) {
    return (
      <Navigate
        to={`/reporting/overview${location.search}${
          location.pathname.endsWith('/goals')
            ? '#goals'
            : location.pathname.endsWith('/programs')
            ? '#programs'
            : location.pathname.endsWith('/feedback')
            ? '#feedback'
            : ''
        }`}
        replace
      />
    );
  }

  if (location.pathname.startsWith('/reporting/overview')) {
    return <ManagerDashboard />;
  }

  return <LegacyCompanyReporting {...props} />;
};
